import { FC, useMemo, useRef, useState } from "react"
import { QESTextInput, QESButton } from "./qes-standards"
import { checkForEnter, scrubUserInput } from "./utility";

interface SearchInputProps {
    descriptiveText?: string;
    escapeSearchValue?: boolean;
    fieldsetClass?: string;
    inputLabel: string;
    placeholder?: string;
    onSubmit: (value: string) => void;
    testId: string;
    alwaysShowClearButton?: boolean;
}

export const SearchBox: FC<React.PropsWithChildren<SearchInputProps>> = (props: SearchInputProps) => {
    const inputField = useRef<HTMLInputElement>(null);
    const [showClearButton, setShowClearButton] = useState(false);

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        checkForEnter(e, () => props.onSubmit(props.escapeSearchValue ? scrubUserInput(inputField.current.value) : inputField.current.value));
    };

    const handleChange = () => {
        // and otherwise we just handle the clear button
        setShowClearButton(props.alwaysShowClearButton || (inputField.current?.value?.length > 0));
    }

    /** 
     * I built this svg using <line/> and ran it through an optimizer, so I'll document what it means here...
     * <path />'s d attribute means "definition" of the shape
     * m moves the "pen" to the relative coordinate pair (starting at 0, 0), and subsequent pairs are interpreted as implicit "draw a line" commands (i.e. "l")
     * so this starts at 0, 0 and draws a line to 100, 100 (so the opposite corner of the viewBox)
     * then m moves the pen to 0, -100 relative to 100, 100 (resulting in 100, 0)
     * and L draws a line from that coordinate to the absolute coordinate 0, 100
     * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d
     */
    const closeX = useMemo(() => (
        <span
            className="d-inline-flex align-items-center qes-text-input__clear-button"
            onClick={() => {
                props.onSubmit("");
                inputField.current.value = "";
                inputField.current.focus();
                setShowClearButton(false);
            }}
        >
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path stroke="#32323c" strokeWidth="5" d="m0 0 100 100m0-100L0 100" /></svg>
        </span>
    ), []);

    return (
        <fieldset className={`qes-searchbox ${props.fieldsetClass}`}>
            <QESTextInput
                testId={`${props.testId}__input`}
                defaultValue=""
                qesRef={inputField}
                label={props.inputLabel}
                onKeyUp={(e) => handleKeyUp(e)}
                onChange={handleChange}
                containerClassName={`col-11 ${!!props.descriptiveText ? "" : "mb-3"}`}
                className="bg-white"
                descriptiveText={props.descriptiveText}
                inputAppend={(props.alwaysShowClearButton || showClearButton) && closeX}
                placeholder={props.placeholder}
            />
            <div className="col-1 d-inline-flex justify-content-center align-items-center">
                <QESButton
                    qesType="secondary"
                    onClick={() => props.onSubmit(props.escapeSearchValue ? scrubUserInput(inputField.current.value) : inputField.current.value)}
                    qesTestId={`${props.testId}__button`}
                >
                    Search
                </QESButton>
            </div>
        </fieldset>
    )
}